import * as React from 'react';
import { FieldType, FormFieldData } from './formHelpers';
import { AllAutomationReleaseTypes, AppPlatform, AutomationReleaseTypes, AutomationType } from '@/types';
import { isGooglePlatform, isIosPlatform } from '@/helpers';
import { displayTextForAutomationReleaseType } from './displayHelpers';

// Parses a string containing markdown-style links [text](url) and returns React elements.
// This is used to render links in things like automation status messages so we can redirect users
// to the relevant documentation, or external URLs.
export const renderTextWithMarkdownLinks = (text: string): React.ReactNode => {
    if (!text) {
        return text;
    }

    // Regular expression to match markdown links: [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = markdownLinkRegex.exec(text)) !== null) {
        const [fullMatch, linkText, url] = match;
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            parts.push(text.substring(lastIndex, matchIndex));
        }

        parts.push(
            <a
                key={`link-${matchIndex}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline' }}
            >
                {linkText}
            </a>,
        );

        lastIndex = matchIndex + fullMatch.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

export const renderReleaseTypesForAutomation = (
    automationType: AutomationType,
    platform: AppPlatform,
    disabled: boolean,
    initialValue?: AutomationReleaseTypes,
): FormFieldData | null => {
    const releaseTypesField: FormFieldData = {
        label: 'Enabled for selected release types',
        name: 'automationReleaseTypes',
        helpMessage: null,
        fieldType: FieldType.Select,
        data: AllAutomationReleaseTypes.map((releaseType) => ({
            value: releaseType,
            displayValue: displayTextForAutomationReleaseType(releaseType),
        })),
        rules: [],
        disabled,
        initialValue,
        extraFormItemProps: {
            validateTrigger: false,
        },
    };

    switch (automationType) {
        case AutomationType.AutoStagedRollout:
            if (isGooglePlatform(platform) || isIosPlatform(platform)) {
                releaseTypesField.initialValue = initialValue || getDefaultReleaseTypesForAutomation(automationType);
                return releaseTypesField;
            }
            return null;
        case AutomationType.AutoSelectLatestBuild:
            if (isGooglePlatform(platform) || isIosPlatform(platform)) {
                releaseTypesField.initialValue = initialValue || getDefaultReleaseTypesForAutomation(automationType);
                return releaseTypesField;
            }
            return null;
        case AutomationType.AutoSyncAppleProvisioningProfilesDevices:
        case AutomationType.AutoSyncAppStoreUsers:
        case AutomationType.AutoSyncSlackGroups:
        case AutomationType.AutoSyncTeamsTags:
        case AutomationType.AutoTranslateUserReviews:
        case AutomationType.BAAutoGenerateBuildTesterNotes:
        case AutomationType.BAAutoTriggerBuildSystemWorkflow:
        case AutomationType.BAAutoUploadBinaryArtifactToAppStore:
        case AutomationType.BAAutoPostBuildInfoOnPMTickets:
        case AutomationType.BAAutoUpdatePRWithArtifactLink:
        case AutomationType.BAAutoAssignBetaBuilds:
            return null;
        default:
            releaseTypesField.initialValue = initialValue || getDefaultReleaseTypesForAutomation(automationType);
            return releaseTypesField;
    }
};

export const getDefaultReleaseTypesForAutomation = (automationType: AutomationType): AutomationReleaseTypes => {
    switch (automationType) {
        case AutomationType.AutoSelectLatestBuild:
        case AutomationType.AutoKickoffRelease:
        case AutomationType.AutoBumpVersion:
        case AutomationType.AutoKickoffTriggerWorkflow:
        case AutomationType.AutoPrepareRollbackRelease:
        case AutomationType.AutoSubmitRelease:
        case AutomationType.AutoRelease:
            return AutomationReleaseTypes.AllExceptHotfix;
        default:
            return AutomationReleaseTypes.All;
    }
};

export enum AutomationSettingsContext {
    App = 'app',
    Release = 'release',
}
